/**
 * useReportStatusCache - Locally-tracked report status (commented/closed/reopened)
 *
 * Extracted from useAdminReports.js. Merges the fetched report list with the
 * AsyncStorage cache of statuses changed on this device since the last fetch,
 * so list rows reflect actions taken on the details screen without a refetch.
 */

import { useState, useCallback } from 'react';

import {
  getCommentedReports,
  getClosedReports,
  getReopenedReports,
  remove,
} from '../util/storage';

const useReportStatusCache = () => {
  const [commentedReports, setCommentedReports] = useState([]);
  const [closedReports, setClosedReports] = useState([]);
  const [reopenedReports, setReopenedReports] = useState([]);

  /**
   * Update cache to determine commented/closed reports
   */
  const updateCache = useCallback(async (reports) => {
    const cache = await getCommentedReports();
    const closedCache = await getClosedReports();
    const reopenedCache = await getReopenedReports();

    const newCommented = [];
    const newClosed = [];
    const newReopened = [];

    if (!reports?.length) return false;

    reports.forEach((report) => {
      const isOriginalCommented = !(
        report.comments === 'No comments yet' || report.comments.length === 0
      );
      const isCacheCommented = cache?.includes(report.uuid);

      if (closedCache?.includes(report.uuid)) {
        newClosed.push(report.uuid);
      }
      if (reopenedCache?.includes(report.uuid)) {
        newReopened.push(report.uuid);
      }
      if (isOriginalCommented || isCacheCommented) {
        newCommented.push(report.uuid);
      }
    });

    setCommentedReports(newCommented);
    setClosedReports(newClosed);
    setReopenedReports(newReopened);
  }, []);

  /**
   * Clear the persisted status flags and local closed/reopened state.
   * Called on each fresh (non-loadMore) ticket fetch, since the fetched data
   * already reflects those changes.
   */
  const clearStatusFlags = useCallback(async () => {
    await remove('reportChanged');
    await remove('reportCommented');
    await remove('reportClosed');
    await remove('reportReopened');
    setClosedReports([]);
    setReopenedReports([]);
  }, []);

  /**
   * Check if report has comments
   */
  const hasComments = useCallback((element) => {
    return (
      (element.comments !== 'No comments yet' && element.comments?.length > 0) ||
      commentedReports.includes(element.uuid)
    );
  }, [commentedReports]);

  /**
   * Check if report is closed
   */
  const isClosed = useCallback((element) => {
    if (reopenedReports.includes(element.uuid)) return false;
    if (closedReports.includes(element.uuid)) return true;
    return element.isClosed;
  }, [reopenedReports, closedReports]);

  return {
    commentedReports,
    closedReports,
    reopenedReports,
    updateCache,
    clearStatusFlags,
    hasComments,
    isClosed,
  };
};

export default useReportStatusCache;
